<script lang="ts" setup>
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import { computed, onBeforeUnmount, onMounted } from '#imports'
  import useDraggable, { UseDraggableProps } from '~/composables/Draggable/Draggable'
  import { MatchFacet } from '~/model/TaskMatch/TaskMatch'

  interface Props extends UseBemProps, UseDraggableProps {
    facets?: Array<string>
    draggableId: string
  }

  const props = defineProps<Props>()
  const { rootEl, isDragging, handleDragStart, handleDragEnd, mountDragImage, unmountDragImage } = useDraggable(props)
  const { bemFacets, bemAdd } = useBem('c-match-draggable', props, {})

  const rootClasses = computed<Array<string>>(() => {
    return [...bemFacets.value, bemAdd(isDragging.value ? 'is-dragging' : '')]
  })

  const onDragStart = (e: DragEvent) => {
    handleDragStart(e)
  }

  const onDragEnd = (e: DragEvent) => {
    handleDragEnd(e)
  }

  onMounted(() => {
    mountDragImage(props.facets?.includes(MatchFacet.IsTextOnly) ? 240 : 160)
  })

  onBeforeUnmount(() => {
    unmountDragImage()
  })
</script>

<template>
  <div
    ref="rootEl"
    :class="rootClasses"
    class="c-match-draggable"
    draggable="true"
    @dragend="onDragEnd($event)"
    @dragstart="onDragStart($event)"
  >
    <div class="c-match-draggable__grab-icon" />
    <div class="c-match-draggable__spacer">
      <div class="c-match-draggable__positionner">
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use 'sass:map';
  @use 'sass:math';
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/transition' as trs;
  @use '/components/Ui/UiIcon/UiIcon' as UiIcon;

  $aspect-ratio: math.div(16, 9);
  $icon-size: map.get(UiIcon.$icon-sizes, 'small') * 0.9;
  $icon-background-size: $icon-size * 0.5;

  .c-match-draggable {
    pointer-events: var(--c-match-draggable-pointer-events, auto);
    position: relative;
    cursor: grab;
    border: 1px solid transparent;
    color: col.$monochrome-white;
    background-color: col.$monochrome-black;

    @include utils.has-focus {
      .c-match-draggable__grab-icon {
        opacity: 1;
      }
    }
  }

  .c-match-draggable--is-dragging {
    cursor: grabbing;
  }

  .c-match-draggable--in-staging-zone {
    border: 1px solid col.$variant-red-dark;
  }

  .c-match-draggable--in-drop-zone {
    border: 1px solid col.$variant-cyan-light;
  }

  .c-match-draggable--is-text-only {
    .c-match-draggable__spacer {
      height: 40px;
    }

    .c-match-draggable__positionner {
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 4px 0 ($icon-size + 4px);
    }
  }

  .c-match-draggable--is-image-only {
    .c-match-draggable__spacer {
      width: 100%;
      padding-top: math.div(100%, $aspect-ratio);
    }

    .c-match-draggable__positionner {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .c-match-draggable__grab-icon {
    @include utils.is-selectable(false);
    pointer-events: none;
    z-index: 1;
    position: absolute;
    top: 50%;
    left: $icon-size * 0.1;
    width: $icon-size;
    height: $icon-size * 1.5;
    transform: translateY(-50%);
    backface-visibility: hidden;
    background-image: radial-gradient(col.$monochrome-tornado 34%, transparent 34%);
    background-size: $icon-background-size $icon-background-size;
    opacity: 0.4;
    transition: opacity trs.$default;
  }

  .c-match-draggable__spacer {
    pointer-events: none;
    position: relative;
  }
</style>
